import { setupClient } from "web_myplann/Infrastructure/Api/Api";
import AppInner from "web_myplann/Infrastructure/AppInner";
import { API_CONTRACT_ID } from "web_myplann/Infrastructure/apiContractId";
import { useToaster } from "web_myplann/Infrastructure/Hooks/useToaster/useToaster";
import { handleMyPlannResponseError } from "web_myplann/Infrastructure/handleMyPlannResponseError";
import "web_myplann/Infrastructure/sentry.ts";
import { CssBaseline } from "@mui/material";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { MutationCache, QueryCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import React, { Suspense, useEffect, useState } from "react";
import ColorSchemeProvider from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import { initI18n } from "web_myplann/Infrastructure/Translations";
import {
	MyPlannQueryClient,
	PERSISTED_QUERY_CACHE_KEY,
	shouldDehydrateMyPlannQuery,
} from "web_myplann/Shared/QueryClient";
import { getMyPlannTheme } from "web_myplann/Shared/Themes";

// Expose setupClient globally for E2E testing
if (typeof window !== "undefined") {
	(window as any).setupClient = setupClient;
}

const LEGACY_RIGHTS_QUERY_KEY = ["rights"] as const;

const persister = createSyncStoragePersister({
	storage: window.localStorage,
	key: PERSISTED_QUERY_CACHE_KEY,
});

const persistOptions = {
	persister,
	// Discard data persisted under a different API contract
	// (see Infrastructure/apiContractId.ts).
	buster: API_CONTRACT_ID,
	dehydrateOptions: {
		shouldDehydrateQuery: shouldDehydrateMyPlannQuery,
	},
};

const queryClient: MyPlannQueryClient = new MyPlannQueryClient({
	queryCache: new QueryCache({
		onError: (error) => handleMyPlannResponseError(error, queryClient),
	}),
	mutationCache: new MutationCache({
		onError: (error) => handleMyPlannResponseError(error, queryClient),
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
			gcTime: 2_147_483_647, // ~24.8 days
		},
	},
});

const ReactQueryDevtoolsProduction = React.lazy(() =>
	import("@tanstack/react-query-devtools/build/modern/production.js").then(
		(d) => ({ default: d.ReactQueryDevtools }),
	),
);

export const App: React.FunctionComponent = () => {
	const [showDevtools, setShowDevtools] = useState(
		import.meta.env.VITE_ALLOW_DEVTOOLS === "true",
	);

	useEffect(() => {
		if (showDevtools) {
			window.toggleDevtools = () => setShowDevtools((old) => !old);
		}
	}, []);

	useToaster();

	initI18n();

	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={persistOptions}
			onSuccess={() => {
				queryClient.removeQueries({ queryKey: LEGACY_RIGHTS_QUERY_KEY });
				if (navigator.onLine) {
					queryClient.resumePausedMutations();
				}
			}}
		>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
			{showDevtools && (
				<Suspense fallback={null}>
					<ReactQueryDevtoolsProduction
						initialIsOpen={false}
						buttonPosition="bottom-left"
					/>
				</Suspense>
			)}
			<ColorSchemeProvider getTheme={getMyPlannTheme}>
				<CssBaseline enableColorScheme />
				<AppInner />
			</ColorSchemeProvider>
		</PersistQueryClientProvider>
	);
};
